import OpenAI from 'openai';
import { OPENAI_CONFIG } from '../../config/openai';
import { detectLanguage } from '../../utils/languageUtils';
import { database } from '../../data/database';
import { createSystemPrompt, getRelevantContext } from '../../utils/promptUtils';
import { AIResponse } from '../../types/openai';
import { formattingService } from '../chat/FormattingService';
import { useDebugStore } from '../../store/debugStore';

const openai = new OpenAI({
  apiKey: OPENAI_CONFIG.apiKey,
  dangerouslyAllowBrowser: true
});

interface GenerateResponseOptions {
  shouldFollowUp?: boolean;
  relevantInfo?: any;
}

export async function generateResponse(
  userMessage: string,
  options: GenerateResponseOptions = {}
): Promise<AIResponse> {
  const { shouldFollowUp, relevantInfo } = options;
  try {
    const detectedLanguage = detectLanguage(userMessage);
    
    // Try the assistant API first
    try {
      return await generateWithAssistant(userMessage, detectedLanguage);
    } catch (assistantError) {
      console.error('Assistant API Error:', assistantError);
      // Log the error to debug store
      const debugStore = useDebugStore.getState();
      debugStore.addLog('error', {
        type: 'assistant',
        message: assistantError instanceof Error ? assistantError.message : 'Unknown error occurred',
        details: assistantError
      });
      
      // Fall back to fine-tuned model with vector store context
      return await generateWithVectorContext(userMessage, detectedLanguage);
    }
  } catch (error) {
    console.error('OpenAI API Error:', error);
    // Log the error to debug store
    const debugStore = useDebugStore.getState();
    debugStore.addLog('error', {
      message: error instanceof Error ? error.message : 'Unknown error occurred',
      details: error
    });
    
    const language = detectLanguage(userMessage);
    return {
      content: language === 'nl' 
        ? 'Sorry, er is een fout opgetreden. Probeer het opnieuw.'
        : 'Sorry, an error occurred. Please try again.',
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

async function generateWithVectorContext(
  userMessage: string,
  language: string
): Promise<AIResponse> {
  const debugStore = useDebugStore.getState();
  
  // Get minimal system prompt
  const systemPrompt = createSystemPrompt(database, language);
  
  // Add specific instructions for handling links and emails
  const linkInstructions = `
IMPORTANT: When including URLs or email addresses in your response:
1. For websites, use the format: [website name or URL](https://website.url)
2. For email addresses, use the format: [email@example.com](mailto:email@example.com)
3. Never use plain text URLs or email addresses - always make them clickable links
4. Do not use bullet points (•) at the beginning of lines
5. Do not include any JavaScript code in your responses
6. Do not include any onClick handlers or other JavaScript in your links
7. For our website, use: [www.eye-catchingmedia.com](https://www.eye-catchingmedia.com)
`;
  
  // Get relevant context from vector store
  const relevantContext = await getRelevantContext(userMessage);
  
  // Explicitly use the model ID from config
  const modelId = OPENAI_CONFIG.model;
  
  // Create messages array
  const messages: { role: 'system' | 'user'; content: string }[] = [
    { role: 'system', content: systemPrompt },
    { role: 'system', content: linkInstructions }
  ];
  
  // Add relevant context if available
  if (relevantContext) {
    messages.push({
      role: 'system',
      content: relevantContext
    });
  }
  
  // Add user message
  messages.push({
    role: 'user',
    content: userMessage
  });
  
  // Prepare request data
  const requestData = {
    model: modelId,
    temperature: OPENAI_CONFIG.temperature,
    max_tokens: OPENAI_CONFIG.maxTokens,
    messages: messages
  };
  
  // Log the request with explicit model ID
  debugStore.addLog('request', {
    type: 'vector-context',
    model: modelId,
    modelId: modelId,
    userMessage,
    systemPromptLength: systemPrompt.length,
    hasRelevantContext: !!relevantContext,
    requestData
  });
  
  // Make the API call with explicit model ID
  const response = await openai.chat.completions.create(requestData);

  if (!response.choices[0]?.message?.content) {
    throw new Error('No response from OpenAI');
  }

  // Log the response with model information
  debugStore.addLog('response', {
    type: 'vector-context',
    model: response.model,
    requestedModel: modelId,
    usage: response.usage,
    rawContent: response.choices[0].message.content
  });

  const content = response.choices[0].message.content;
  const formattedContent = formattingService.formatMessage(content);

  return {
    content: formattedContent,
    error: undefined
  };
}

// Use assistant API as primary method
async function generateWithAssistant(
  userMessage: string,
  language: string
): Promise<AIResponse> {
  try {
    const debugStore = useDebugStore.getState();
    
    // Explicitly use the assistant ID from config
    const assistantId = OPENAI_CONFIG.assistantId;
    
    if (!assistantId) {
      throw new Error('Assistant ID is not configured');
    }
    
    // Add specific instructions for handling links and emails
    const enhancedMessage = `${userMessage}

IMPORTANT: When including URLs or email addresses in your response:
1. For websites, use the format: [website name or URL](https://website.url)
2. For email addresses, use the format: [email@example.com](mailto:email@example.com)
3. Never use plain text URLs or email addresses - always make them clickable links
4. Do not use bullet points (•) at the beginning of lines
5. Do not include any JavaScript code in your responses
6. Do not include any onClick handlers or other JavaScript in your links
7. For our website, use: [www.eye-catchingmedia.com](https://www.eye-catchingmedia.com)`;
    
    // Log the request with explicit assistant ID
    debugStore.addLog('request', {
      type: 'assistant',
      message: enhancedMessage,
      assistantId: assistantId
    });
    
    // Create a thread
    const thread = await openai.beta.threads.create();
    
    // Add a message to the thread
    await openai.beta.threads.messages.create(thread.id, {
      role: "user",
      content: enhancedMessage
    });
    
    // Run the assistant on the thread with explicit assistant ID
    const run = await openai.beta.threads.runs.create(thread.id, {
      assistant_id: assistantId
    });
    
    // Poll for the run to complete
    let runStatus = await openai.beta.threads.runs.retrieve(thread.id, run.id);
    
    // Wait for the run to complete (with timeout)
    const startTime = Date.now();
    const timeout = 30000; // 30 seconds timeout
    
    while (runStatus.status !== 'completed' && runStatus.status !== 'failed') {
      // Check for timeout
      if (Date.now() - startTime > timeout) {
        throw new Error('Assistant response timed out');
      }
      
      // Wait a bit before checking again
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check status again
      runStatus = await openai.beta.threads.runs.retrieve(thread.id, run.id);
    }
    
    if (runStatus.status === 'failed') {
      const errorMessage = runStatus.last_error?.message || 'Unknown error';
      debugStore.addLog('error', {
        type: 'assistant',
        message: errorMessage,
        runStatus
      });
      throw new Error(`Assistant run failed: ${errorMessage}`);
    }
    
    // Get the messages from the thread
    const messages = await openai.beta.threads.messages.list(thread.id);
    
    // Find the last assistant message
    const assistantMessages = messages.data.filter(msg => msg.role === 'assistant');
    
    if (assistantMessages.length === 0) {
      throw new Error('No assistant response received');
    }
    
    // Get the content from the last assistant message
    const lastMessage = assistantMessages[0];
    let content = '';
    
    if (lastMessage.content && lastMessage.content.length > 0) {
      const textContent = lastMessage.content.filter(item => item.type === 'text');
      if (textContent.length > 0 && 'text' in textContent[0]) {
        content = textContent[0].text.value;
      }
    }
    
    if (!content) {
      throw new Error('No text content in assistant response');
    }
    
    // Log the response
    debugStore.addLog('response', {
      type: 'assistant',
      threadId: thread.id,
      runId: run.id,
      assistantId: assistantId,
      rawContent: content
    });
    
    // Format the content
    const formattedContent = formattingService.formatMessage(content);
    
    return {
      content: formattedContent,
      error: undefined
    };
  } catch (error) {
    console.error('Assistant API error:', error);
    throw error; // Rethrow to fall back to the vector context model
  }
}

export const openAIService = {
  generateResponse
};
